import React, { useEffect, useMemo, useRef, useState } from 'react'

import { parseBulkTokens } from './EditDealModal/helpers'

interface DeviceMultiSelectProps {
  catalog: string[]
  selected: string[]
  onChange: (nextSelected: string[], nextCatalog: string[]) => void
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  width?: number
  inputPlaceholder: string
}

export const DeviceMultiSelect: React.FunctionComponent<DeviceMultiSelectProps> = (props) => {
  const { catalog, selected, onChange, isOpen, onOpenChange, width, inputPlaceholder } = props
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const [search, setSearch] = useState('')
  const [typed, setTyped] = useState('')

  useEffect(() => {
    if (!isOpen) return undefined
    const handler = (event: MouseEvent) => {
      if (!wrapRef.current) return
      if (!wrapRef.current.contains(event.target as Node)) onOpenChange(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [isOpen, onOpenChange])

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return catalog
    return catalog.filter((entry) => entry.toLowerCase().includes(query))
  }, [catalog, search])

  function toggle(device: string): void {
    if (selected.includes(device)) onChange(selected.filter((entry) => entry !== device), catalog)
    else onChange([...selected, device], catalog)
  }

  function addTokens(tokens: string[]): void {
    if (tokens.length === 0) return
    const lowerCatalog = new Map(catalog.map((entry) => [entry.toLowerCase(), entry]))
    const lowerSelected = new Set(selected.map((entry) => entry.toLowerCase()))
    const nextCatalog = [...catalog]
    const nextSelected = [...selected]
    for (const token of tokens) {
      const key = token.toLowerCase()
      const existing = lowerCatalog.get(key)
      if (existing) {
        if (!lowerSelected.has(key)) {
          nextSelected.push(existing)
          lowerSelected.add(key)
        }
      } else {
        nextCatalog.push(token)
        lowerCatalog.set(key, token)
        nextSelected.push(token)
        lowerSelected.add(key)
      }
    }
    onChange(nextSelected, nextCatalog)
  }

  function commitTyped(): void {
    const tokens = parseBulkTokens(typed)
    if (tokens.length === 0) {
      setTyped('')
      return
    }
    addTokens(tokens)
    setTyped('')
  }

  return (
    <div className="device-select-wrap" ref={wrapRef}>
      <input
        className="device-select-input"
        placeholder={inputPlaceholder}
        autoComplete="off"
        spellCheck={false}
        value={typed}
        onClick={() => {
          if (!isOpen) onOpenChange(true)
        }}
        onChange={(event) => setTyped(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ',' || event.key === 'Tab') {
            if (typed.trim()) {
              event.preventDefault()
              commitTyped()
            }
          }
        }}
        onBlur={() => {
          if (typed.trim()) commitTyped()
        }}
        onPaste={(event) => {
          const text = event.clipboardData.getData('text')
          const tokens = parseBulkTokens(text)
          if (tokens.length > 1) {
            event.preventDefault()
            addTokens(tokens)
            setTyped('')
          }
        }}
      />
      <div
        className={`device-dropdown${isOpen ? ' open' : ''}`}
        style={width ? { width } : undefined}
      >
        <div className="device-dropdown-search">
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        {filtered.map((device) => (
          <label className="device-dropdown-item" key={device}>
            <input
              type="checkbox"
              checked={selected.includes(device)}
              onChange={() => toggle(device)}
            />{' '}
            {device}
          </label>
        ))}
      </div>
    </div>
  )
}
